/**
 * Subscription Service
 *
 * This service provides business logic for managing office subscriptions
 * and enforcing user capacity limits based on subscription tiers.
 *
 * Key responsibilities:
 * - Retrieve subscription information for offices
 * - Calculate capacity usage and remaining slots
 * - Validate whether new users can be added to an office
 * - Provide subscription status information for UI display
 */

import { getTierById } from '../config/subscriptionTiers'
import db, { getOffice, getOfficeSubscription } from '../db/db'
import Database from '../types/Database'

/**
 * Capacity information for an office
 */
export interface CapacityInfo {
  currentUsers: number
  capacity: number
  tier: Database.SubscriptionTier
  remaining: number
  isOverLimit?: boolean // True when subscription lapsed with more than Free tier limit users
}

/**
 * Get the subscription for a specific office
 *
 * @param officeId - The office ID to get the subscription for
 * @returns The subscription record or null if not found (defaults to free tier)
 */
export async function getSubscription(
  officeId: string
): Promise<Database.Subscription | null> {
  return await getOfficeSubscription(officeId)
}

/**
 * Get capacity information for an office
 * Returns current user count, total capacity, tier, and remaining slots
 *
 * @param officeId - The office ID to get capacity info for
 * @returns Capacity information object
 * @throws Error if office not found
 */
export async function getCapacityInfo(officeId: string): Promise<CapacityInfo> {
  // Get office with subscription data
  const office = await getOffice(officeId)

  if (!office) {
    throw new Error(`Office not found: ${officeId}`)
  }

  // Get the tier and capacity (defaults to free tier if not set)
  const tier = office.subscriptionTier || 'free'
  const capacity = office.userCapacity || getTierById('free').userCapacity

  // Count current users in the office
  // Users are associated with offices via the officeids array field
  const currentUsers = await countOfficeUsers(officeId)

  // Calculate remaining slots
  const remaining = Math.max(0, capacity - currentUsers)

  const result: CapacityInfo = {
    currentUsers,
    capacity,
    tier,
    remaining
  }

  // Only include isOverLimit if it's true
  if (office.isOverLimit) {
    result.isOverLimit = true
  }

  return result
}

/**
 * Check if a new user can be added to an office
 * Validates against the office's current subscription tier capacity limit
 *
 * Also checks if the office is in an "over limit" state (subscription lapsed
 * while having more than Free tier limit users). In this case, new additions
 * are blocked even if the capacity shows available slots.
 *
 * @param officeId - The office ID to check
 * @returns true if a user can be added, false if at or over capacity or if over limit
 */
export async function canAddUser(officeId: string): Promise<boolean> {
  const office = await getOffice(officeId)

  if (!office) {
    throw new Error(`Office not found: ${officeId}`)
  }

  // If office is in over-limit state (lapsed subscription with too many users),
  // block new additions regardless of capacity calculation
  if (office.isOverLimit) {
    return false
  }

  const capacityInfo = await getCapacityInfo(officeId)

  // Can add user if there are remaining slots
  return capacityInfo.remaining > 0
}

/**
 * Count the number of users currently in an office
 * Users are associated with offices via the officeids array field
 *
 * @param officeId - The office ID to count users for
 * @returns The number of users in the office
 */
async function countOfficeUsers(officeId: string): Promise<number> {
  try {
    // Query users where officeids array contains this office ID
    const users = await db.query({
      collection: 'users',
      where: ['officeids', 'array-contains', officeId]
    })

    return users.length
  } catch (error) {
    console.error(`Error counting users for office ${officeId}:`, error)
    // Return 0 on error to fail safe (allow operations)
    return 0
  }
}

/**
 * Validate if a user can be added to an office
 * Returns detailed validation result with reason if blocked
 *
 * @param officeId - The office ID to validate
 * @returns Validation result with allowed flag, reason, and capacity info
 */
export async function validateUserAddition(officeId: string): Promise<{
  allowed: boolean
  reason?: string
  currentCount: number
  capacity: number
  tier: Database.SubscriptionTier
  isOverLimit?: boolean
}> {
  const office = await getOffice(officeId)

  if (!office) {
    throw new Error(`Office not found: ${officeId}`)
  }

  const capacityInfo = await getCapacityInfo(officeId)

  // Check if office is in over-limit state
  if (office.isOverLimit) {
    return {
      allowed: false,
      reason: `This office's subscription has lapsed and currently has more than the Free tier limit of 5 users. New users cannot be added until the user count drops to 5 or below, or you upgrade your subscription.`,
      currentCount: capacityInfo.currentUsers,
      capacity: capacityInfo.capacity,
      tier: capacityInfo.tier,
      isOverLimit: true
    }
  }

  const allowed = capacityInfo.remaining > 0

  const result: {
    allowed: boolean
    reason?: string
    currentCount: number
    capacity: number
    tier: Database.SubscriptionTier
    isOverLimit?: boolean
  } = {
    allowed,
    reason: allowed
      ? undefined
      : `Office has reached its user capacity limit of ${
          capacityInfo.capacity
        } users for the ${getTierById(capacityInfo.tier).name} tier.`,
    currentCount: capacityInfo.currentUsers,
    capacity: capacityInfo.capacity,
    tier: capacityInfo.tier
  }

  return result
}

/**
 * Get offices owned by a specific user
 * Used for subscription purchase flow to show which offices the user can upgrade
 *
 * @param userId - The user ID (office owner)
 * @returns Array of offices owned by the user
 */
export async function getOwnedOffices(
  userId: string
): Promise<Database.Office[]> {
  try {
    const offices = await db.query({
      collection: 'offices',
      where: ['uid', '==', userId]
    })

    return offices
  } catch (error) {
    console.error(`Error fetching owned offices for user ${userId}:`, error)
    return []
  }
}

/**
 * Get subscription status including grace period information
 *
 * @param officeId - The office ID to get subscription status for
 * @returns Subscription status information
 * @throws Error if office not found
 */
export async function getSubscriptionStatus(officeId: string): Promise<{
  tier: Database.SubscriptionTier
  status: Database.SubscriptionStatus
  billingInterval?: 'monthly' | 'annual'
  gracePeriodEnd?: Date
  gracePeriodDaysRemaining?: number
  currentPeriodEnd?: Date
}> {
  // Get office to determine tier
  const office = await getOffice(officeId)

  if (!office) {
    throw new Error(`Office not found: ${officeId}`)
  }

  // Get subscription record
  const subscription = await getOfficeSubscription(officeId)

  // If no subscription exists, return free tier with active status
  if (!subscription) {
    return {
      tier: office.subscriptionTier || 'free',
      status: 'active'
    }
  }

  // Build the response object
  const result: {
    tier: Database.SubscriptionTier
    status: Database.SubscriptionStatus
    billingInterval?: 'monthly' | 'annual'
    gracePeriodEnd?: Date
    gracePeriodDaysRemaining?: number
    currentPeriodEnd?: Date
  } = {
    tier: subscription.tier,
    status: subscription.status,
    billingInterval: subscription.billingInterval,
    currentPeriodEnd: subscription.currentPeriodEnd
  }

  // Add grace period information if in grace period
  if (subscription.gracePeriodEnd && isInGracePeriod(subscription)) {
    result.gracePeriodEnd = subscription.gracePeriodEnd
    result.gracePeriodDaysRemaining = calculateGracePeriodDays(
      subscription.gracePeriodEnd
    )
  }

  return result
}

/**
 * Create a Stripe Checkout Session for subscription purchase
 *
 * This function calls the Firebase Cloud Function to create a Stripe Checkout Session.
 * The session redirects the user to Stripe's hosted checkout page.
 *
 * @param officeId - The office to apply the subscription to
 * @param tier - The subscription tier ('small', 'medium', or 'large')
 * @param billingInterval - The billing interval ('monthly' or 'annual')
 * @returns The Checkout Session URL to redirect the user to
 * @throws Error if checkout session creation fails
 */
export async function createCheckoutSession(
  officeId: string,
  tier: Database.SubscriptionTier,
  billingInterval: 'monthly' | 'annual' = 'monthly'
): Promise<string> {
  // Validate tier is a paid tier
  if (tier === 'free') {
    throw new Error('Cannot create checkout session for free tier')
  }

  try {
    // Import Firebase functions dynamically to avoid circular dependencies
    const { getFunctions, httpsCallable } = await import('firebase/functions')
    const functions = getFunctions()

    // Call the Cloud Function
    const createCheckout = httpsCallable<
      { officeId: string; tier: string; billingInterval: string },
      { sessionId: string; url: string }
    >(functions, 'createCheckoutSession')

    const result = await createCheckout({ officeId, tier, billingInterval })

    if (!result.data.url) {
      throw new Error('No checkout URL returned from server')
    }

    return result.data.url
  } catch (error: any) {
    console.error('Error creating checkout session:', error)
    throw new Error(
      error.message || 'Failed to create checkout session. Please try again.'
    )
  }
}

/**
 * Create a Stripe Customer Portal Session for subscription management
 *
 * This function calls the Firebase Cloud Function to create a Customer Portal Session.
 * The portal allows users to manage subscriptions, update payment methods, and view invoices.
 *
 * @param officeId - The office whose subscription to manage
 * @returns The Customer Portal URL to redirect the user to
 * @throws Error if portal session creation fails
 */
export async function createPortalSession(officeId: string): Promise<string> {
  try {
    // Import Firebase functions dynamically to avoid circular dependencies
    const { getFunctions, httpsCallable } = await import('firebase/functions')
    const functions = getFunctions()

    // Call the Cloud Function
    const createPortal = httpsCallable<{ officeId: string }, { url: string }>(
      functions,
      'createPortalSession'
    )

    const result = await createPortal({ officeId })

    if (!result.data.url) {
      throw new Error('No portal URL returned from server')
    }

    return result.data.url
  } catch (error: any) {
    console.error('Error creating portal session:', error)
    throw new Error(
      error.message || 'Failed to access billing portal. Please try again.'
    )
  }
}

/**
 * Update an office's subscription tier and capacity
 *
 * This function handles tier changes for both upgrades and downgrades:
 * - Upgrades: Applied immediately upon successful payment
 * - Downgrades: Applied at the end of the current billing cycle
 *
 * The function checks the subscription's billing cycle to determine
 * whether to apply the change immediately or defer it.
 *
 * @param officeId - The office ID to update
 * @param newTier - The new subscription tier to apply
 * @param currentPeriodEnd - Optional billing cycle end date (for deferred downgrades)
 * @returns Promise that resolves when the update is complete
 * @throws Error if office not found or update fails
 */
export async function updateOfficeTier(
  officeId: string,
  newTier: Database.SubscriptionTier,
  currentPeriodEnd?: Date
): Promise<void> {
  try {
    // Get the current office data
    const office = await getOffice(officeId)

    if (!office) {
      throw new Error(`Office not found: ${officeId}`)
    }

    // Get the current tier (defaults to free if not set)
    const currentTier = office.subscriptionTier || 'free'

    // Determine if this is an upgrade or downgrade
    const tierOrder: Record<Database.SubscriptionTier, number> = {
      free: 0,
      small: 1,
      medium: 2,
      large: 3
    }

    const isUpgrade = tierOrder[newTier] > tierOrder[currentTier]
    const isDowngrade = tierOrder[newTier] < tierOrder[currentTier]

    // Get the new capacity for the tier
    const tierConfig = getTierById(newTier)
    const newCapacity = tierConfig.userCapacity

    // Determine if we should apply the change immediately
    let shouldApplyImmediately = true

    if (isDowngrade && currentPeriodEnd) {
      // For downgrades, check if we're still within the billing cycle
      const now = new Date()
      const periodEnd = new Date(currentPeriodEnd)

      // If the billing cycle hasn't ended yet, defer the update
      if (now < periodEnd) {
        shouldApplyImmediately = false
        console.log(
          `Downgrade from ${currentTier} to ${newTier} will be applied at end of billing cycle: ${periodEnd.toISOString()}`
        )
        // Note: In a production system, you would schedule this update
        // using a Cloud Function triggered at the billing cycle end
        // For now, we just log it and return without updating
        return
      }
    }

    // Apply the tier change immediately (for upgrades or downgrades at cycle end)
    if (shouldApplyImmediately) {
      console.log(
        `${
          isUpgrade ? 'Upgrading' : isDowngrade ? 'Downgrading' : 'Updating'
        } office ${officeId} from ${currentTier} to ${newTier}`
      )

      // Update the office document with new tier and capacity
      await db.updateItem('offices', officeId, {
        subscriptionTier: newTier,
        userCapacity: newCapacity
      })

      console.log(
        `Office ${officeId} tier updated to ${newTier} with capacity ${newCapacity}`
      )
    }
  } catch (error) {
    console.error(`Error updating office tier for ${officeId}:`, error)
    throw new Error(
      `Failed to update office tier: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}

/**
 * Calculate days remaining in grace period
 *
 * @param gracePeriodEnd - The date when the grace period expires
 * @returns Number of days remaining (0 if expired, rounded up for partial days)
 */
export function calculateGracePeriodDays(gracePeriodEnd: Date): number {
  const now = new Date()
  const end = new Date(gracePeriodEnd)

  // Calculate difference in milliseconds
  const diffMs = end.getTime() - now.getTime()

  // If grace period has expired, return 0
  if (diffMs <= 0) {
    return 0
  }

  // Convert to days and round up (partial days count as full days)
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  return diffDays
}

/**
 * Check if subscription is currently in grace period
 *
 * @param subscription - The subscription to check
 * @returns true if subscription is in grace period, false otherwise
 */
export function isInGracePeriod(subscription: Database.Subscription): boolean {
  // No grace period if gracePeriodEnd is not set
  if (!subscription.gracePeriodEnd) {
    return false
  }

  // Check if grace period has not expired yet
  const now = new Date()
  const end = new Date(subscription.gracePeriodEnd)

  return now < end
}

const subscriptionService = {
  getSubscription,
  getCapacityInfo,
  canAddUser,
  validateUserAddition,
  getOwnedOffices,
  createCheckoutSession,
  createPortalSession,
  updateOfficeTier,
  calculateGracePeriodDays,
  isInGracePeriod,
  getSubscriptionStatus
}

export default subscriptionService
